import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Info, ChevronDown, ChevronUp, Lightbulb, Check } from "lucide-react";

interface PageAboutProps {
  title: string;
  description: string;
  features?: string[];
  tips?: string[];
}

export function PageAbout({ title, description, features, tips }: PageAboutProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { t } = useTranslation();

  return (
    <Card className="bg-muted/30 border-dashed" data-testid="card-page-about">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Info className="h-4 w-4 text-primary" />
            <CardTitle className="text-sm font-medium">{t('page-about.about-prefix', { title })}</CardTitle>
          </div>
          <Button
            variant="ghost"
            size="default"
            className="min-h-[44px]"
            onClick={() => setIsExpanded(!isExpanded)}
            data-testid="button-toggle-about"
          >
            {isExpanded ? (
              <>
                <span className="text-xs mr-1">Hide</span>
                <ChevronUp className="h-4 w-4" />
              </>
            ) : (
              <>
                <span className="text-xs mr-1">Learn More</span>
                <ChevronDown className="h-4 w-4" />
              </>
            )}
          </Button>
        </div>
        <p className="text-sm text-muted-foreground">{description}</p>
      </CardHeader>
      {isExpanded && (
        <CardContent className="pt-0 space-y-4">
          {features && features.length > 0 && (
            <div>
              <h4 className="text-sm font-medium mb-2">{t('page-about.key-features')}</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                {features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <Check className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {tips && tips.length > 0 && (
            <div>
              <h4 className="text-sm font-medium mb-2">{t('page-about.tips')}</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                {tips.map((tip, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <Lightbulb className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" />
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}
